// src/components/Register/Register.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../supabase';
import './Register.css';

const Register = () => {
  // ===== ALL HOOKS =====
  const [activeCategory, setActiveCategory] = useState('sponsorship');
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [cartAnimating, setCartAnimating] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPolicyAgreement, setShowPolicyAgreement] = useState(false);
  const [showBankDetails, setShowBankDetails] = useState(false);
  
  // Checkout form fields
  const [payeeName, setPayeeName] = useState('');
  const [institution, setInstitution] = useState('');
  const [institutionEmail, setInstitutionEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  
  // Referral code state
  const [referralCode, setReferralCode] = useState('');
  
  // Policy agreement states
  const [policyAgreed, setPolicyAgreed] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [policyLoading, setPolicyLoading] = useState(false);
  const [policyError, setPolicyError] = useState('');

  // ===== CAPTURE REFERRAL CODE FROM URL =====
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const ref = urlParams.get('ref');
    
    console.log('🔍 Checking for referral code in URL...');
    console.log('🔍 URL params:', window.location.search);
    console.log('🔍 Ref parameter:', ref);
    
    if (ref) {
      console.log('✅ Referral code captured from URL:', ref);
      setReferralCode(ref);
      sessionStorage.setItem('referralCode', ref);
    } else {
      const savedRef = sessionStorage.getItem('referralCode');
      if (savedRef) {
        console.log('✅ Referral code from session:', savedRef);
        setReferralCode(savedRef);
      } else {
        console.log('ℹ️ No referral code found');
      }
    }
  }, []);

  // ===== HELPER FUNCTIONS =====
  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  // ===== CATEGORIES =====
  const categories = [
    { id: 'sponsorship', label: 'Sponsorship' },
    { id: 'diplomatic', label: 'Diplomatic' },
    { id: 'exhibition', label: 'Exhibition' },
    { id: 'participation', label: 'Participation' },
    { id: 'ngos', label: 'NGOs, CSOs & Small Businesses' }
  ];

  // ===== SPONSORSHIP PACKAGES =====
  const sponsorshipPackages = [
    {
      id: 1,
      title: 'Full Title Sponsorship',
      price: 50000000,
      formattedPrice: '₦50,000,000',
      image: '/reg-img/Full-Title.png',
      badge: 'FULL TITLE',
      benefits: [
        'Event co-naming rights (e.g., "AICIS 2025 powered by [Your Company]")',
        'Prominent logo placement on all event materials, including banners, brochures, and digital platforms',
        'Keynote speaking slot during the opening ceremony',
        'VIP networking access to exclusive sessions with heads of state, ministers, and industry leaders',
        'Premium 36 sqm exhibition booth in a prime location',
        'Media exposure in press releases, interviews, and media coverage',
        '5 complimentary delegate passes with VIP seating',
        'Full-page advertorial in the summit magazine',
        'Exclusive Certificate of Participation'
      ]
    },
    {
      id: 2,
      title: 'Co-Title Sponsorship',
      price: 25000000,
      formattedPrice: '₦25,000,000',
      image: '/reg-img/Co-Title.png',
      badge: 'CO-TITLE',
      benefits: [
        'Co-branding rights on select event materials',
        'Logo placement on banners, brochures, and digital platforms',
        '3 slots in a high-profile panel discussion',
        'Invitations to VIP receptions and dinners',
        'Premium 18 sqm exhibition booth',
        'Inclusion in press releases and media coverage',
        '3 complimentary delegate passes with priority seating',
        'Half-page advertorial in the summit magazine',
        'VIP Certificate of Participation'
      ]
    },
    {
      id: 3,
      title: 'Companies | Institutions',
      price: 15000000,
      formattedPrice: '₦15,000,000',
      image: '/reg-img/companies.png',
      badge: 'COMPANIES',
      benefits: [
        'Logo placement on event signage and digital platforms',
        '2 slots to join a thematic panel discussion',
        'Invitations to VIP receptions and dinners',
        'Standard 9 sqm exhibition booth',
        'Mention in press releases',
        '2 complimentary delegate passes',
        'Quarter-page advertorial in the summit magazine',
        'Certificate of Participation'
      ]
    }
  ];

  // ===== DIPLOMATIC PACKAGES =====
  const diplomaticPackages = [
    {
      id: 4,
      title: 'Embassies | High Commissions | Diplomats | International Organizations',
      price: 2000000,
      formattedPrice: '₦2,000,000',
      image: '/reg-img/strategic.png',
      badge: 'DIPLOMATIC',
      benefits: [
        'Complimentary speaking slot during the Opening Ceremony for the Ambassador or an official representative',
        'Two high-level speaking opportunities on strategic topics related to economic development, trade facilitation, and climate action',
        'Official logo placement on the main conference banner and other event promotional materials',
        'One fully equipped 12 sqm exhibition booth within the designated Diplomatic Missions Pavilion',
        'Two (2) complimentary full-access summit passes for official delegates',
        'Placement in a specially designated area for embassies, high commissions, and international organizations'
      ]
    }
  ];

  // ===== EXHIBITION PACKAGES =====
  const exhibitionPackages = [
    {
      id: 5,
      title: 'Platinum Exhibition',
      price: 7650000,
      formattedPrice: '₦7,650,000',
      image: '/reg-img/platinum.png',
      badge: 'PLATINUM',
      boothSize: '36 sqm premium space',
      location: 'Prime position near main entrances or high-traffic areas',
      branding: 'Enhanced visibility with company logo on event materials',
      delegatePasses: '4 complimentary passes',
      networking: 'Access to VIP networking events',
      summitMaterials: 'Full-page advert in the summit magazine'
    },
    {
      id: 6,
      title: 'Diamond Exhibition',
      price: 5950000,
      formattedPrice: '₦5,950,000',
      image: '/reg-img/diamond.png',
      badge: 'DIAMOND',
      boothSize: '24 sqm premium space',
      location: 'Strategic position within the exhibition hall',
      branding: 'Company logo on select event materials',
      delegatePasses: '3 complimentary passes',
      networking: 'Invitations to selected networking events',
      summitMaterials: 'Half-page advert in the summit magazine'
    },
    {
      id: 7,
      title: 'Gold Exhibition',
      price: 4250000,
      formattedPrice: '₦4,250,000',
      image: '/reg-img/gold.png',
      badge: 'GOLD',
      boothSize: '18 sqm standard space',
      location: 'Central area within the exhibition hall',
      branding: 'Company name listed on event materials',
      delegatePasses: '2 complimentary passes',
      networking: 'Access to general networking sessions',
      summitMaterials: 'Quarter-page advert in the summit magazine'
    },
    {
      id: 8,
      title: 'Corporate Bodies',
      price: 1500000,
      formattedPrice: '₦1,500,000',
      image: '/reg-img/Corporate.png',
      badge: 'CORPORATE',
      boothSize: '10 sqm',
      location: 'Standard exhibition area',
      branding: 'Company logo on select event materials',
      delegatePasses: '1 complimentary pass',
      networking: 'General networking access',
      summitMaterials: 'Conference kit'
    },
    {
      id: 9,
      title: 'Government Agencies',
      price: 1000000,
      formattedPrice: '₦1,000,000',
      image: '/reg-img/govt-agency.png',
      badge: 'GOVERNMENT',
      boothSize: '9 sqm',
      location: 'Government pavilion',
      branding: 'Company name listed on event materials',
      delegatePasses: '1 complimentary pass',
      networking: 'General networking access',
      summitMaterials: 'Conference kit'
    },
    {
      id: 10,
      title: 'Prime Exhibition (SMEs | NGOs Only)',
      price: 500000,
      formattedPrice: '₦500,000',
      image: '/reg-img/prime.png',
      badge: 'PRIME',
      boothSize: '6 sqm',
      location: 'SME | NGO designated area',
      delegatePasses: '1 complimentary pass',
      branding: 'Basic branding',
      networking: 'General networking access',
      summitMaterials: 'Conference kit'
    },
    {
      id: 11,
      title: 'Standard Exhibition (SMEs | NGOs Only)',
      price: 300000,
      formattedPrice: '₦300,000',
      image: '/reg-img/Standard.png',
      badge: 'STANDARD',
      boothSize: '4 sqm',
      location: 'SME | NGO designated area',
      delegatePasses: '1 complimentary pass',
      branding: 'Basic branding',
      networking: 'General networking access',
      summitMaterials: 'Conference kit'
    }
  ];

  // ===== PARTICIPATION PACKAGES =====
  const participationPackages = [
    {
      id: 12,
      title: 'International Attendee',
      price: 2890000,
      formattedPrice: '₦2,890,000',
      image: '/reg-img/intl-att.png',
      badge: 'INTERNATIONAL',
      code: 'AICIS2026-INT-0001',
      benefits: [
        'Standard room accommodation for 3 nights',
        'Airport pick-up and drop-off transportation',
        'Selected access to plenary sessions, workshops, and exhibitions',
        'Conference kit and summit magazine'
      ]
    },
    {
      id: 13,
      title: 'Local Corporate Organization',
      price: 2690000,
      formattedPrice: '₦2,690,000',
      image: '/reg-img/local-org.png',
      badge: 'CORPORATE',
      code: 'AICIS2026-LOC-0001',
      benefits: [
        'Standard room accommodation for 3 nights',
        'Selected access to plenary sessions, workshops, and exhibitions',
        'Conference kit and summit magazine'
      ]
    }
  ];

  // ===== NGOs, CSOs & SMALL BUSINESSES PACKAGES =====
  const ngosPackages = [
    {
      id: 14,
      title: 'Local SMEs | NGOs (1 Day Ticket)',
      price: 150000,
      formattedPrice: '₦150,000',
      image: '/reg-img/ngo.png',
      badge: '1 DAY',
      code: 'AICIS2026-SME-0001',
      benefits: [
        'Access to sessions and exhibitions for one day',
        'Conference kit'
      ]
    },
    {
      id: 15,
      title: 'Local SMEs | NGOs (2 Day Ticket)',
      price: 250000,
      formattedPrice: '₦250,000',
      image: '/reg-img/ngo2.png',
      badge: '2 DAY',
      code: 'AICIS2026-SME-0002',
      benefits: [
        'Access to sessions and exhibitions for two days',
        'Conference kit'
      ]
    },
    {
      id: 16,
      title: 'Local SMEs | NGOs (3 Day Ticket)',
      price: 300000,
      formattedPrice: '₦300,000',
      image: '/reg-img/ngo3.png',
      badge: '3 DAY',
      code: 'AICIS2026-SME-0003',
      benefits: [
        'Access to all sessions and exhibitions for three days',
        'Conference kit'
      ]
    }
  ];

  // ===== BANK TRANSFER DETAILS =====
  const getBankDetails = () => {
    const amount = Math.round(getTotalPrice() * 1.075);
    return {
      zenith: {
        bankName: 'Zenith Bank',
        accountNumber: '1017297933',
        accountName: 'AFRICA BUSINESS AND INVST INTL NIG',
      },
      fcmb: {
        bankName: 'FCMB',
        accountNumber: '2003951724',
        accountName: 'Africa Business Ventures and Investments International, Nig Ltd.',
        branch: 'Gudu Branch, Abuja, FCT',
        md: 'Moses OT Owharo',
        phone: '07032941519'
      },
      amount: amount
    };
  };

  // ===== ADD TO CART WITH AUTO-OPEN =====
  const addToCart = (pkg) => {
    const existingItem = cart.find(item => item.id === pkg.id);
    if (existingItem) {
      setCart(cart.map(item => 
        item.id === pkg.id ? { ...item, quantity: item.quantity + 1 } : item
      ));
    } else {
      setCart([...cart, { ...pkg, quantity: 1 }]);
    }
    setShowCart(true);
    setCartAnimating(true);
    setTimeout(() => setCartAnimating(false), 500);
  };

  const removeFromCart = (id) => {
    setCart(cart.filter(item => item.id !== id));
  };

  const updateQuantity = (id, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(id);
    } else {
      setCart(cart.map(item => 
        item.id === id ? { ...item, quantity: newQuantity } : item
      ));
    }
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      alert('Your cart is empty. Please add items before checkout.');
      return;
    }
    setShowPaymentModal(true);
  };

  // ===== UPDATE AFFILIATE STATS =====
  const updateAffiliateStats = async (referralCode) => {
    try {
      console.log('📊 Updating affiliate stats for code:', referralCode);
      
      // Get affiliate by referral code
      const { data: affiliate, error: fetchError } = await supabase
        .from('affiliates')
        .select('*')
        .eq('referral_code', referralCode)
        .single();

      if (fetchError) {
        console.error('Error fetching affiliate:', fetchError);
        return;
      }

      if (affiliate) {
        // Get total referrals from affiliate_purchases
        const { data: purchases, error: purchasesError } = await supabase
          .from('affiliate_purchases')
          .select('amount')
          .eq('referral_code', referralCode);

        if (purchasesError) {
          console.error('Error fetching purchases:', purchasesError);
          return;
        }

        const totalReferrals = purchases?.length || 0;
        const totalEarnings = purchases?.reduce((sum, p) => sum + (p.amount || 0), 0) * 0.05;

        // Update affiliate
        const { error: updateError } = await supabase
          .from('affiliates')
          .update({
            total_referrals: totalReferrals,
            total_earnings: totalEarnings || 0,
            updated_at: new Date().toISOString()
          })
          .eq('id', affiliate.id);

        if (updateError) {
          console.error('Error updating affiliate:', updateError);
        } else {
          console.log('✅ Affiliate stats updated!');
        }
      }
    } catch (error) {
      console.error('Error updating affiliate stats:', error);
    }
  };

  // ===== SAVE TRANSACTION AND REFERRAL TO SUPABASE =====
  const saveTransactionToSupabase = async (status, reference = null, method = 'bank_transfer') => {
    try {
      const currentReferralCode = referralCode || sessionStorage.getItem('referralCode') || null;
      
      console.log('🔍 Saving transaction with referral code:', currentReferralCode);
      
      const transactionRef = reference || 'AICIS-' + Date.now().toString().slice(-8);
      const totalAmount = Math.round(getTotalPrice() * 1.075);
      const itemsList = cart.map(item => `${item.title} x${item.quantity}`).join(', ');
      const packageTitles = cart.map(item => item.title).join(', ');
      const packagePrices = cart.map(item => item.formattedPrice).join(', ');

      // 1. Save to transactions table
      const transactionData = {
        transaction_id: transactionRef,
        customer_name: payeeName || 'Guest',
        customer_email: institutionEmail || 'guest@email.com',
        customer_phone: phoneNumber || 'N/A',
        institution: institution || 'N/A',
        amount: totalAmount,
        items: itemsList,
        status: status,
        payment_method: method,
        reference: reference || null,
        referral_code: currentReferralCode
      };

      console.log('💾 Saving to transactions:', transactionData);

      const { error: transactionError } = await supabase
        .from('transactions')
        .insert([transactionData]);

      if (transactionError) {
        console.error('❌ Error saving transaction:', transactionError);
        return false;
      }
      console.log('✅ Transaction saved successfully');

      // 2. If there's a referral code, save to affiliate_purchases table
      if (currentReferralCode) {
        console.log('🔍 Saving referral to affiliate_purchases for code:', currentReferralCode);
        
        const affiliateData = {
          transaction_id: transactionRef,
          referral_code: currentReferralCode,
          full_name: payeeName || 'Guest',
          email: institutionEmail || 'guest@email.com',
          phone: phoneNumber || 'N/A',
          organization: institution || 'N/A',
          package_title: packageTitles,
          package_price: packagePrices,
          status: 'pending',
          amount: totalAmount,
          commission_rate: 0.05
        };

        console.log('💾 Saving to affiliate_purchases:', affiliateData);

        const { error: affiliateError } = await supabase
          .from('affiliate_purchases')
          .insert([affiliateData]);

        if (affiliateError) {
          console.error('❌ Error saving affiliate purchase:', affiliateError);
          // Don't return false - transaction was saved
        } else {
          console.log('✅ Affiliate purchase saved successfully!');
          
          // Update affiliate stats
          await updateAffiliateStats(currentReferralCode);
        }
      }

      return true;
    } catch (err) {
      console.error('❌ Error:', err);
      return false;
    }
  };

  // ===== SAVE POLICY AGREEMENT TO SUPABASE =====
  const savePolicyAgreement = async () => {
    try {
      const currentReferralCode = referralCode || sessionStorage.getItem('referralCode');
      
      const agreementData = {
        email: institutionEmail.trim(),
        full_name: payeeName.trim(),
        institution: institution.trim(),
        phone: phoneNumber.trim(),
        agreed_at: new Date().toISOString(),
        ip_address: 'N/A',
        user_agent: navigator.userAgent,
        status: 'agreed',
        policy_version: '1.0',
        has_scrolled: true,
        scroll_percentage: 100,
        referral_code: currentReferralCode || null
      };

      const { error } = await supabase
        .from('policy_agreements')
        .insert([agreementData])
        .select();

      if (error) {
        console.error('Policy agreement error:', error);
        return false;
      }

      console.log('Policy agreement saved with referral:', currentReferralCode);
      return true;
    } catch (error) {
      console.error('Error saving policy agreement:', error);
      return false;
    }
  };

  // ===== POLICY AGREEMENT HANDLERS =====
  const handlePolicyScroll = (e) => {
    const element = e.target;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight - element.clientHeight;
    
    if (scrollTop >= scrollHeight - 50) {
      setHasScrolledToBottom(true);
    } else {
      setHasScrolledToBottom(false);
    }
  };

  const handlePolicyAgree = async () => {
    if (!policyAgreed) {
      alert('Please tick the checkbox to confirm you agree.');
      return;
    }
    if (!hasScrolledToBottom) {
      alert('Please scroll to the bottom of the policy document.');
      return;
    }

    setPolicyLoading(true);
    setPolicyError('');

    try {
      const saved = await savePolicyAgreement();
      
      if (saved) {
        sessionStorage.setItem('policyAgreed', 'true');
        sessionStorage.setItem('policyEmail', institutionEmail);
        sessionStorage.setItem('policyAgreedAt', new Date().toISOString());
      }

      setShowPolicyAgreement(false);
      setShowBankDetails(true);
      setPolicyAgreed(false);
      setHasScrolledToBottom(false);
      
    } catch (error) {
      setPolicyError('Something went wrong. Please try again.');
      console.error('Policy agreement error:', error);
    } finally {
      setPolicyLoading(false);
    }
  };

  // ===== PAYSTACK & MONNIFY UNDER MAINTENANCE =====
  const handleMaintenanceClick = () => {
    alert('🔧 Paystack & Monnify are currently under maintenance.\n\nPlease use Bank Transfer instead.\n\nWe apologize for the inconvenience.');
  };

  // ===== BANK TRANSFER - Shows Policy Agreement First =====
  const handleBankTransfer = async () => {
    if (!payeeName) {
      alert('Please enter your full name.');
      return;
    }
    if (!institution) {
      alert('Please enter your institution or organization name.');
      return;
    }
    if (!institutionEmail) {
      alert('Please enter your institution email address.');
      return;
    }
    if (!phoneNumber) {
      alert('Please enter your phone number.');
      return;
    }
    if (cart.length === 0) {
      alert('Your cart is empty. Please add items before checkout.');
      return;
    }

    setShowPaymentModal(false);
    setShowPolicyAgreement(true);
  };

  // ===== BANK TRANSFER CONFIRM WITH WHATSAPP REDIRECT =====
  const handleBankTransferConfirm = async () => {
    const transactionRef = 'AICIS-BT-' + Date.now().toString().slice(-8);
    
    const currentReferralCode = referralCode || sessionStorage.getItem('referralCode');
    
    // Save transaction AND referral to affiliate_purchases
    const saved = await saveTransactionToSupabase('pending_confirmation', transactionRef, 'bank_transfer');
    
    if (!saved) {
      alert('❌ Failed to save transaction. Please try again.');
      return;
    }
    
    const bankDetails = getBankDetails();
    const amount = bankDetails.amount;
    
    // WhatsApp number
    const whatsappNumber = '+2348142638552';
    
    const message = encodeURIComponent(
      `✅ AICIS 2026 Payment Receipt\n\n` +
      `Reference: ${transactionRef}\n` +
      `Name: ${payeeName}\n` +
      `Amount: ₦${amount.toLocaleString()}\n` +
      `Institution: ${institution}\n` +
      `Email: ${institutionEmail}\n` +
      `Phone: ${phoneNumber}\n` +
      `${currentReferralCode ? `🔗 Referral Code: ${currentReferralCode}` : ''}\n\n` +
      `📎 Please send a picture of the payment receipt here.`
    );

    // Clear cart and close modals
    setCart([]);
    setShowPaymentModal(false);
    setShowCart(false);
    setShowBankDetails(false);
    setPayeeName('');
    setInstitution('');
    setInstitutionEmail('');
    setPhoneNumber('');
    
    // Show bank details
    alert(
      `✅ Bank Transfer initiated!\n\n` +
      `Please transfer the exact amount to:\n\n` +
      `━━━━━━━━━━━━━━━━━━━━━━━━━\n` +
      `🏦 OPTION 1: ZENITH BANK\n` +
      `📋 Account: ${bankDetails.zenith.accountNumber}\n` +
      `👤 ${bankDetails.zenith.accountName}\n\n` +
      `━━━━━━━━━━━━━━━━━━━━━━━━━\n` +
      `🏦 OPTION 2: FCMB\n` +
      `📋 Account: ${bankDetails.fcmb.accountNumber}\n` +
      `👤 ${bankDetails.fcmb.accountName}\n` +
      `📍 ${bankDetails.fcmb.branch}\n` +
      `👤 MD: ${bankDetails.fcmb.md}\n` +
      `📞 Phone: ${bankDetails.fcmb.phone}\n\n` +
      `━━━━━━━━━━━━━━━━━━━━━━━━━\n` +
      `💰 Amount: ₦${amount.toLocaleString()}\n\n` +
      `📱 Click OK to send your payment receipt via WhatsApp.`
    );

    // Open WhatsApp after alert
    setTimeout(() => {
      window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
    }, 500);
  };

  const renderPackages = () => {
    let packages = [];
    switch(activeCategory) {
      case 'sponsorship':
        packages = sponsorshipPackages;
        break;
      case 'diplomatic':
        packages = diplomaticPackages;
        break;
      case 'exhibition':
        packages = exhibitionPackages;
        break;
      case 'participation':
        packages = participationPackages;
        break;
      case 'ngos':
        packages = ngosPackages;
        break;
      default:
        packages = [];
    }

    return (
      <div className="register-packages-grid">
        {packages.map(pkg => (
          <div className={`register-package-card ${pkg.id === 4 ? 'featured' : ''}`} key={pkg.id}>
            <div className="package-image-container">
              <img src={pkg.image} alt={pkg.title} className="package-full-image" />
              <div className="package-badge">{pkg.badge}</div>
              <div className="package-price-ribbon">{pkg.formattedPrice}</div>
            </div>
            <div className="package-content">
              <h3>{pkg.title}</h3>
              {pkg.benefits ? (
                <ul className="package-benefits">
                  {pkg.benefits.map((benefit, index) => (
                    <li key={index}>
                      <span className="benefit-icon">✓</span>
                      {benefit}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="package-details">
                  <div className="detail-item">
                    <span className="detail-label">Booth Size:</span>
                    <span className="detail-value">{pkg.boothSize}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Location:</span>
                    <span className="detail-value">{pkg.location}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Branding:</span>
                    <span className="detail-value">{pkg.branding}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Delegate Passes:</span>
                    <span className="detail-value">{pkg.delegatePasses}</span>
                  </div>
                  {pkg.networking && (
                    <div className="detail-item">
                      <span className="detail-label">Networking:</span>
                      <span className="detail-value">{pkg.networking}</span>
                    </div>
                  )}
                  {pkg.summitMaterials && (
                    <div className="detail-item">
                      <span className="detail-label">Summit Materials:</span>
                      <span className="detail-value">{pkg.summitMaterials}</span>
                    </div>
                  )}
                </div>
              )}
              <button 
                className="package-register-btn"
                onClick={() => addToCart(pkg)}
              >
                Add to Cart
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="register-page">
      {/* ===== FLOATING CART ===== */}
      <div 
        className={`floating-cart ${cart.length > 0 ? 'has-items' : ''}`}
        onClick={() => setShowCart(!showCart)}
      >
        <div className="floating-cart-icon">
          🛒
          {cart.length > 0 && (
            <span className={`cart-badge ${cartAnimating ? 'bounce' : ''}`}>
              {getTotalItems()}
            </span>
          )}
        </div>
        <span className="floating-cart-text">
          {cart.length > 0 ? `₦${getTotalPrice().toLocaleString()}` : 'Cart'}
        </span>
        <span className="floating-cart-arrow">{showCart ? '▲' : '▼'}</span>
      </div>

      {/* ===== HERO ===== */}
      <div className="register-hero">
        <div className="register-hero-overlay"></div>
        <div className="register-hero-content">
          <span className="register-hero-badge">REGISTER NOW</span>
          <h1>Summit Registration</h1>
          <h2>Choose Your Participation Package</h2>
          <p>
            Secure your place at AICIS 2026. Select from our comprehensive range of 
            sponsorship, exhibition, and participation packages designed to maximize 
            your summit experience.
          </p>
        </div>
      </div>

      {/* ===== REFERRAL CODE DISPLAY ===== */}
      {referralCode && (
        <div className="referral-display">
          <span className="referral-label">🔗 Referred by:</span>
          <span className="referral-code">{referralCode}</span>
        </div>
      )}

      {/* ===== TABS ===== */}
      <div className="register-tabs">
        <div className="register-tabs-container">
          {categories.map(cat => (
            <button
              key={cat.id}
              className={`register-tab ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* ===== PACKAGES ===== */}
      <div className="register-content">
        <div className="register-content-inner">
          {renderPackages()}
        </div>
      </div>

      {/* ===== CART ===== */}
      <div className={`register-cart-section ${showCart && cart.length > 0 ? 'open' : ''}`}>
        <div className="register-cart-container">
          <div className="register-cart-header">
            <h2>
              🛒 Your Cart 
              {cart.length > 0 && <span className="cart-count">{getTotalItems()}</span>}
            </h2>
            {cart.length > 0 && (
              <button 
                className="cart-toggle-btn"
                onClick={() => setShowCart(!showCart)}
              >
                {showCart ? 'Hide Cart' : 'Show Cart'}
              </button>
            )}
          </div>

          {showCart && (
            <div className="register-cart-content">
              {cart.length === 0 ? (
                <p className="empty-cart">Your cart is empty. Select a package to get started.</p>
              ) : (
                <>
                  <div className="cart-items">
                    {cart.map(item => (
                      <div className="cart-item" key={item.id}>
                        <div className="cart-item-info">
                          <h4>{item.title}</h4>
                          <span className="cart-item-price">{item.formattedPrice}</span>
                        </div>
                        <div className="cart-item-actions">
                          <button 
                            className="qty-btn"
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          >
                            −
                          </button>
                          <span className="qty-number">{item.quantity}</span>
                          <button 
                            className="qty-btn"
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          >
                            +
                          </button>
                          <button 
                            className="remove-btn"
                            onClick={() => removeFromCart(item.id)}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="cart-total">
                    <div className="cart-total-row">
                      <span>Subtotal:</span>
                      <span>₦{getTotalPrice().toLocaleString()}</span>
                    </div>
                    <div className="cart-total-row">
                      <span>VAT (7.5%):</span>
                      <span>₦{Math.round(getTotalPrice() * 0.075).toLocaleString()}</span>
                    </div>
                    <div className="cart-total-row grand-total">
                      <span>Total:</span>
                      <span>₦{Math.round(getTotalPrice() * 1.075).toLocaleString()}</span>
                    </div>
                    <button className="checkout-btn" onClick={handleCheckout}>
                      Proceed to Checkout
                    </button>
                    <p className="payment-note">
                      ⚠️ Paystack & Monnify are currently under maintenance. Please use Bank Transfer.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ===== PAYMENT MODAL ===== */}
      {showPaymentModal && !showBankDetails && !showPolicyAgreement && (
        <div className="payment-modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
            <button className="payment-modal-close" onClick={() => setShowPaymentModal(false)}>×</button>
            
            <div className="payment-modal-header">
              <span className="payment-modal-badge">SECURE CHECKOUT</span>
              <h2>Complete Your Purchase</h2>
              <p>Enter your details to complete the registration</p>
            </div>

            <div className="payment-modal-body">
              <div className="payment-summary">
                <div className="payment-summary-row">
                  <span>Total Amount:</span>
                  <span className="payment-amount">₦{Math.round(getTotalPrice() * 1.075).toLocaleString()}</span>
                </div>
                <div className="payment-summary-row small">
                  <span>Items:</span>
                  <span>{getTotalItems()} package(s)</span>
                </div>
              </div>

              <div className="payment-customer-details">
                <div className="form-group">
                  <label>Payee Full Name *</label>
                  <input
                    type="text"
                    placeholder="Enter your full name"
                    value={payeeName}
                    onChange={(e) => setPayeeName(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Institution / Organization *</label>
                  <input
                    type="text"
                    placeholder="Enter your institution or organization name"
                    value={institution}
                    onChange={(e) => setInstitution(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Institution Email Address *</label>
                  <input
                    type="email"
                    placeholder="Enter your institution email address"
                    value={institutionEmail}
                    onChange={(e) => setInstitutionEmail(e.target.value)}
                    required                  />
                </div>

                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    placeholder="Enter your phone number"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="payment-methods">
                <div 
                  className="payment-method-card maintenance"
                  onClick={handleMaintenanceClick}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="payment-method-icon">💳</div>
                  <div className="payment-method-info">
                    <h4>Paystack <span className="maintenance-badge">🔧</span></h4>
                    <p>Currently under maintenance</p>
                    <span className="payment-method-tag maintenance-tag">UNAVAILABLE</span>
                  </div>
                  <span className="payment-method-arrow">⛔</span>
                </div>

                <div 
                  className="payment-method-card maintenance"
                  onClick={handleMaintenanceClick}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="payment-method-icon">🏦</div>
                  <div className="payment-method-info">
                    <h4>Monnify <span className="maintenance-badge">🔧</span></h4>
                    <p>Currently under maintenance</p>
                    <span className="payment-method-tag maintenance-tag">UNAVAILABLE</span>
                  </div>
                  <span className="payment-method-arrow">⛔</span>
                </div>

                <div 
                  className="payment-method-card featured available"
                  onClick={handleBankTransfer}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="payment-method-icon">🏛️</div>
                  <div className="payment-method-info">
                    <h4>Bank Transfer <span className="available-badge">✅</span></h4>
                    <p>Direct bank transfer to our account</p>
                    <span className="payment-method-tag available-tag">AVAILABLE</span>
                  </div>
                  <span className="payment-method-arrow">→</span>
                </div>
              </div>

              <p className="payment-modal-footer">
                🔒 Your payment information is secure and encrypted.
              </p>
              <p className="payment-modal-maintenance-note">
                ⚠️ <strong>Paystack & Monnify are under maintenance.</strong> Please use Bank Transfer.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ===== POLICY AGREEMENT MODAL ===== */}
      {showPolicyAgreement && (
        <div className="policy-agreement-modal-overlay">
          <div className="policy-agreement-modal" onClick={(e) => e.stopPropagation()}>
            <button 
              className="policy-agreement-close" 
              onClick={() => {
                setShowPolicyAgreement(false);
                setShowPaymentModal(true);
                setPolicyAgreed(false);
                setHasScrolledToBottom(false);
              }}
            >
              ×
            </button>
            
            <div className="policy-agreement-header">
              <span className="policy-agreement-badge">📋 POLICY AGREEMENT</span>
              <h2>Terms & Conditions</h2>
              <p>Please read and agree to our terms before proceeding with payment.</p>
            </div>

            <div className="policy-agreement-body">
              <div 
                className="policy-agreement-scroll"
                onScroll={handlePolicyScroll}
              >
                <div className="policy-agreement-content">
                  <h3>1. Cancellation & Refund Policy</h3>
                  <p>
                    The Africa Infrastructure, Climate Change, Security & Green Investment Summit (AICIS) 
                    is organized by <strong>Africa Business Ventures & Investment Group (ABVIG)</strong>.
                  </p>
                  
                  <h4>1.1 Cancellation by Participant</h4>
                  <ul>
                    <li>Cancellation requests must be submitted in writing at least <strong>30 calendar days</strong> before the Summit.</li>
                    <li>Approved cancellations are eligible for a refund of up to <strong>60%</strong> of the registration fee.</li>
                    <li>Requests received less than 30 days before the Summit are not eligible for a refund.</li>
                  </ul>

                  <h4>1.2 Non-Refundable Charges</h4>
                  <ul>
                    <li>Bank transaction and transfer charges</li>
                    <li>International remittance fees</li>
                    <li>SWIFT/Telex charges</li>
                    <li>Payment gateway processing fees</li>
                    <li>VAT and applicable taxes</li>
                  </ul>

                  <h3>2. Visa & Immigration Policy</h3>
                  <p>
                    Upon confirmation of registration and payment, participants may receive:
                  </p>
                  <ul>
                    <li>Official Letter of Invitation</li>
                    <li>Confirmation of Registration</li>
                    <li>Supporting conference documentation</li>
                  </ul>
                  <p className="policy-warning">
                    <strong>⚠️ These documents do not guarantee visa approval.</strong>
                  </p>

                  <h3>3. Participant Conduct Policy</h3>
                  <p>
                    Participants agree to:
                  </p>
                  <ul>
                    <li>Respect the laws of the host country</li>
                    <li>Observe immigration and border control requirements</li>
                    <li>Maintain professional conduct during the Summit</li>
                  </ul>

                  <h3>4. Event Modification & Force Majeure</h3>
                  <p>
                    The Organisers reserve the right to postpone, reschedule, or cancel the Summit due to:
                  </p>
                  <ul>
                    <li>Natural disasters</li>
                    <li>Public health emergencies</li>
                    <li>Civil unrest or security concerns</li>
                    <li>Government directives</li>
                  </ul>

                  <h3>5. Privacy & Data Protection</h3>
                  <p>
                    By registering, participants consent to the collection and use of their personal information 
                    for conference administration, communications, and security purposes.
                  </p>

                  <h3>6. Photography & Media Consent</h3>
                  <p>
                    Participants grant ABVIG and AICIS permission to use photographs and recordings for:
                  </p>
                  <ul>
                    <li>Event reporting</li>
                    <li>Promotional materials</li>
                    <li>Publications</li>
                    <li>Social media</li>
                  </ul>

                  <h3>7. Acceptance of Terms</h3>
                  <p>
                    Registration and payment constitute acceptance of these policies.
                  </p>
                  <p>
                    Participants confirm they have read, understood, and agree to comply with all Summit rules.
                  </p>

                  <div className="policy-scroll-indicator">
                    {hasScrolledToBottom ? (
                      <span className="scroll-done">✓ You've read the full policy</span>
                    ) : (
                      <span className="scroll-more">↓ Scroll down to continue</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="policy-agreement-footer">
                {policyError && (
                  <div className="policy-error">
                    <span className="error-icon">⚠️</span>
                    {policyError}
                  </div>
                )}

                <div className="policy-agreement-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={policyAgreed}
                      onChange={(e) => setPolicyAgreed(e.target.checked)}
                    />
                    <span>I have read, understood, and agree to the policies above.</span>
                  </label>
                </div>

                <button 
                  className="policy-agree-btn"
                  onClick={handlePolicyAgree}
                  disabled={!policyAgreed || !hasScrolledToBottom || policyLoading}
                >
                  {policyLoading ? 'Processing...' : 'I Agree & Continue to Payment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== BANK DETAILS MODAL ===== */}
      {showBankDetails && (
        <div className="payment-modal-overlay" onClick={() => setShowBankDetails(false)}>
          <div className="payment-modal bank-details-modal" onClick={(e) => e.stopPropagation()}>
            <button className="payment-modal-close" onClick={() => setShowBankDetails(false)}>×</button>
            
            <div className="payment-modal-header">
              <span className="payment-modal-badge">🏛️ BANK TRANSFER</span>
              <h2>Complete Your Bank Transfer</h2>
              <p>Please transfer the exact amount to any of the accounts below</p>
            </div>

            <div className="payment-modal-body">
              {(() => {
                const bankDetails = getBankDetails();
                const amount = bankDetails.amount;
                return (
                  <>
                    <div className="bank-details-card">
                      <div className="bank-details-card-header">
                        <span className="bank-badge">OPTION 1</span>
                        <h3>🏦 Zenith Bank</h3>
                      </div>
                      <div className="bank-detail-row">
                        <span className="bank-detail-label">Bank Name</span>
                        <span className="bank-detail-value">{bankDetails.zenith.bankName}</span>
                      </div>
                      <div className="bank-detail-row">
                        <span className="bank-detail-label">Account Number</span>
                        <span className="bank-detail-value highlight">{bankDetails.zenith.accountNumber}</span>
                      </div>
                      <div className="bank-detail-row">
                        <span className="bank-detail-label">Account Name</span>
                        <span className="bank-detail-value">{bankDetails.zenith.accountName}</span>
                      </div>
                    </div>

                    <div className="bank-details-card fcmb-card">
                      <div className="bank-details-card-header">
                        <span className="bank-badge">OPTION 2</span>
                        <h3>🏦 FCMB Bank</h3>
                      </div>
                      <div className="bank-detail-row">
                        <span className="bank-detail-label">Bank Name</span>
                        <span className="bank-detail-value">{bankDetails.fcmb.bankName}</span>
                      </div>
                      <div className="bank-detail-row">
                        <span className="bank-detail-label">Account Number</span>
                        <span className="bank-detail-value highlight">{bankDetails.fcmb.accountNumber}</span>
                      </div>
                      <div className="bank-detail-row">
                        <span className="bank-detail-label">Account Name</span>
                        <span className="bank-detail-value">{bankDetails.fcmb.accountName}</span>
                      </div>
                      <div className="bank-detail-row">
                        <span className="bank-detail-label">Branch</span>
                        <span className="bank-detail-value">{bankDetails.fcmb.branch}</span>
                      </div>
                      <div className="bank-detail-row">
                        <span className="bank-detail-label">Managing Director</span>
                        <span className="bank-detail-value">{bankDetails.fcmb.md}</span>
                      </div>
                      <div className="bank-detail-row">
                        <span className="bank-detail-label">Phone</span>
                        <span className="bank-detail-value">{bankDetails.fcmb.phone}</span>
                      </div>
                    </div>

                    <div className="bank-details-card total-card">
                      <div className="bank-detail-row total">
                        <span className="bank-detail-label">💰 Total Amount</span>
                        <span className="bank-detail-value amount">₦{amount.toLocaleString()}</span>
                      </div>
                    </div>

                    <div className="bank-transfer-instructions">
                      <h4>⚠️ Important Instructions</h4>
                      <ul>
                        <li>Transfer the exact amount shown above to <strong>either account</strong></li>
                        <li>Use your full name as the narration/description</li>
                        <li>Your registration will be confirmed within 24-48 hours</li>
                        <li>You will receive a confirmation email after verification</li>
                      </ul>
                    </div>

                    <button 
                      className="bank-confirm-btn"
                      onClick={handleBankTransferConfirm}
                    >
                      ✅ I Have Made the Transfer
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ===== BACK BUTTON ===== */}
      <div className="register-back">
        <Link to="/" className="back-button">← Back to Home</Link>
      </div>
    </div>
  );
};

export default Register;